import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiZoomIn, FiX, FiChevronLeft, FiChevronRight, FiInstagram } from 'react-icons/fi';
import './Gallery.css';

const photos = [
  { id: 1, category: 'Wedding', url: '/assets/main1.png' },
  { id: 2, category: 'Wedding', url: '/assets/main2.png' },
  { id: 3, category: 'Wedding', url: '/assets/main3.png' },
  { id: 4, category: 'Wedding', url: '/assets/main4.png' },
  { id: 5, category: 'Reception', url: '/assets/main5.png' },
  { id: 6, category: 'Reception', url: '/assets/main6.png' },
  { id: 7, category: 'Stage Decoration', url: '/assets/main7.png' },
  { id: 8, category: 'Stage Decoration', url: '/assets/main8.png' },
  { id: 9, category: 'Stage Decoration', url: '/assets/main9.png' },
  { id: 10, category: 'Dining Area', url: '/assets/main10.png' },
  { id: 11, category: 'Dining Area', url: '/assets/main11.png' },
  { id: 12, category: 'Exterior', url: '/assets/main12.png ' },
  { id: 13, category: 'Exterior', url: '/assets/main13.png' },
  { id: 14, category: 'Night View', url: '/assets/main14.png' },
  { id: 15, category: 'Night View', url: '/assets/main15.png' },
  { id: 16, category: 'Night View', url: '/assets/main16.png' },
  { id: 17, category: 'Interior', url: '/assets/main17.png' },
  { id: 18, category: 'Interior', url: '/assets/main18.png ' },

  { id: 19, category: 'Corporate Events', url: '/assets/main19.png' },
  { id: 20, category: 'Corporate Events', url: '/assets/main20.png' },
  { id: 21, category: 'Corporate Events', url: '/assets/main21.png' },
  { id: 22, category: 'Corporate Events', url: '/assets/main22.png' },
  { id: 23, category: 'Corporate Events', url: '/assets/main23.png' },
  { id: 24, category: 'Corporate Events', url: '/assets/main24.png' },
  { id: 25, category: 'Corporate Events', url: '/assets/main25.png' },
];

const categories = ['All', 'Wedding', 'Reception', 'Corporate Events', 'Stage Decoration', 'Dining Area', 'Exterior', 'Interior', 'Night View'];

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [filteredPhotos, setFilteredPhotos] = useState(photos);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const { ref: parallaxRef, inView: parallaxInView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const { ref: ctaRef, inView: ctaInView } = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredPhotos(photos);
    } else {
      setFilteredPhotos(photos.filter(photo => photo.category === activeCategory));
    }
  }, [activeCategory]);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const goToNextPhoto = useCallback((e) => {
    e?.stopPropagation();
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % filteredPhotos.length);
  }, [filteredPhotos.length]);

  const goToPrevPhoto = useCallback((e) => {
    e?.stopPropagation();
    setCurrentPhotoIndex((prevIndex) => (prevIndex === 0 ? filteredPhotos.length - 1 : prevIndex - 1));
  }, [filteredPhotos.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNextPhoto();
      if (e.key === 'ArrowLeft') goToPrevPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToNextPhoto, goToPrevPhoto]);

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <section className="gallery-hero">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Gallery
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          A Visual Journey Through Prasannalakshmi Kalyana Mandapam & Auditorium
        </motion.p>
      </section>

      {/* Filter Bar */}
      <div className="gallery-filter-wrapper">
        <div className="gallery-filter-bar">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Masonry Gallery */}
      <section className="gallery-masonry-container">
        <motion.div layout className="masonry-grid">
          <AnimatePresence>
            {filteredPhotos.map((photo, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                key={photo.id}
                className="masonry-item"
                onClick={() => openLightbox(index)}
              >
                <img src={photo.url} alt={`${photo.category} showcase`} loading="lazy" />
                <div className="masonry-overlay">
                  <FiZoomIn className="zoom-icon" />
                  <span className="overlay-category">{photo.category}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Parallax Break */}
      <section className="parallax-break" ref={parallaxRef}>
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          animate={parallaxInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Every Corner Tells a Story
        </motion.h2>
      </section>

      {/* Instagram CTA */}
      <section className="instagram-cta" ref={ctaRef}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h3>Share Your Moments</h3>
          <p>Tag us in your celebration photos and join our stories.</p>
          <a href="#" className="btn-social">
            <FiInstagram size={24} />
            Follow on Instagram
          </a>
        </motion.div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <button className="lightbox-close" onClick={closeLightbox}>
              <FiX />
            </button>

            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {filteredPhotos.length > 1 && (
                <button className="lightbox-nav nav-prev" onClick={goToPrevPhoto}>
                  <FiChevronLeft />
                </button>
              )}

              <img
                src={filteredPhotos[currentPhotoIndex].url}
                alt={filteredPhotos[currentPhotoIndex].category}
              />

              {filteredPhotos.length > 1 && (
                <button className="lightbox-nav nav-next" onClick={goToNextPhoto}>
                  <FiChevronRight />
                </button>
              )}

              <div className="lightbox-counter">
                {currentPhotoIndex + 1} / {filteredPhotos.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
